import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaPlus, FaDownload, FaUserPlus,
  FaTimesCircle, FaClock, FaEnvelope, FaIdCard,
  FaPhone, FaEye, FaFileUpload, FaArrowLeft,
  FaMagic, FaEdit, FaChevronDown, FaChevronUp
} from 'react-icons/fa';
import axiosInstance from '../utils/axiosConfig';
import NewEmployeeModal from './NewEmployeeModal';
import './CandidatosKanbanPanel.css';

const CANDIDATO_COLUMNS = [
  { key: 'Nuevo',        label: 'Nuevo',        color: '#64748b', dark: '#334155', light: '#f8fafc' },
  { key: 'En revisión',  label: 'En revisión',  color: '#2563eb', dark: '#1e40af', light: '#dbeafe' },
  { key: 'Entrevistado', label: 'Entrevistado', color: '#7c3aed', dark: '#5b21b6', light: '#ede9fe' },
  { key: 'Seleccionado', label: 'Seleccionado', color: '#059669', dark: '#065f46', light: '#d1fae5' },
];

const getPrioridadStyle = (p) => {
  switch (p) {
    case 'Alta':  return { bg: '#fef2f2', color: '#dc2626' };
    case 'Media': return { bg: '#fffbeb', color: '#d97706' };
    case 'Baja':  return { bg: '#eff6ff', color: '#2563eb' };
    default:      return { bg: '#f3f4f6', color: '#6b7280' };
  }
};

const getDiasAbierta = (fecha) =>
  Math.floor((new Date() - new Date(fecha)) / (1000 * 60 * 60 * 24));

const getCompatColor = (score) => {
  if (score >= 8) return '#059669';
  if (score >= 5) return '#d97706';
  return '#dc2626';
};

const EMPTY_FORM = {
  nombre: '', cedula: '', telefono_personal: '',
  email_personal: '', notas: '', cvFile: null
};

const CandidatosKanbanPanel = ({ vacante, onClose, onVacanteUpdated }) => {
  const [candidatos, setCandidatos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addStep, setAddStep] = useState('upload'); // 'upload' | 'form'
  const [extracting, setExtracting] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formLoading, setFormLoading] = useState(false);
  const [descartandoId, setDescartandoId] = useState(null);
  const [motivoDescarte, setMotivoDescarte] = useState('');
  const [showCreateEmployee, setShowCreateEmployee] = useState(false);
  const [candidatoParaEmpleado, setCandidatoParaEmpleado] = useState(null);
  const [showDescartados, setShowDescartados] = useState(false);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [analyzingId, setAnalyzingId] = useState(null);
  const [expandedAnalysis, setExpandedAnalysis] = useState(null);
  const [comparativeResult, setComparativeResult] = useState(null);
  const [comparativeLoading, setComparativeLoading] = useState(false);
  const [expandedNotes, setExpandedNotes] = useState({});
  const [viewingCV, setViewingCV] = useState(null); // { url, type, filename }

  useEffect(() => {
    if (vacante?.id) fetchCandidatos();
  }, [vacante?.id]);

  const fetchCandidatos = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(`/api/vacantes/${vacante.id}/candidatos`);
      setCandidatos(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updateEstado = async (candidatoId, nuevoEstado, extra = {}) => {
    try {
      await axiosInstance.patch(`/api/candidatos/${candidatoId}`, { estado: nuevoEstado, ...extra });
      fetchCandidatos();
      onVacanteUpdated?.();
    } catch (err) {
      console.error(err);
    }
  };

  // Drag & Drop
  const handleDragStart = (e, id) => {
    setDraggingId(id);
    e.dataTransfer.setData('candidatoId', String(id));
  };

  const handleDragOver = (e, colKey) => {
    e.preventDefault();
    setDragOverCol(colKey);
  };

  const handleDrop = (e, colKey) => {
    e.preventDefault();
    const id = parseInt(e.dataTransfer.getData('candidatoId'));
    const c = candidatos.find(x => x.id === id);
    if (c && c.estado !== colKey) updateEstado(id, colKey);
    setDraggingId(null);
    setDragOverCol(null);
  };

  // Análisis individual con IA
  const handleAnalyze = async (candidatoId) => {
    setAnalyzingId(candidatoId);
    try {
      const res = await axiosInstance.post(`/api/candidatos/${candidatoId}/analyze`);
      setCandidatos(prev => prev.map(c =>
        c.id === candidatoId ? { ...c, analisis_ia: res.data } : c
      ));
      setExpandedAnalysis(candidatoId);
    } catch (err) {
      alert(err.response?.data?.error || 'Error al analizar candidato');
    } finally {
      setAnalyzingId(null);
    }
  };

  // Análisis comparativo (dev only)
  const handleComparative = async () => {
    setComparativeLoading(true);
    try {
      const res = await axiosInstance.post(`/api/vacantes/${vacante.id}/analyze-candidates`);
      setComparativeResult(res.data);
    } catch (err) {
      alert(err.response?.data?.error || 'Error al comparar candidatos');
    } finally {
      setComparativeLoading(false);
    }
  };

  // Extraer datos del CV con IA
  const handleExtractCV = async (file) => {
    setExtracting(true);
    const data = new FormData();
    data.append('cv', file);
    try {
      const res = await axiosInstance.post('/api/extract-cv', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData({
        nombre: res.data.nombre || '',
        cedula: res.data.cedula || '',
        telefono_personal: res.data.telefono_personal || '',
        email_personal: res.data.email_personal || '',
        notas: res.data.notas || '',
        cvFile: file
      });
      setAddStep('form');
    } catch (err) {
      alert(err.response?.data?.error || 'Error al extraer datos del CV');
    } finally {
      setExtracting(false);
    }
  };

  const resetAddForm = () => {
    setShowAddForm(false);
    setAddStep('upload');
    setFormData(EMPTY_FORM);
  };

  // Agregar candidato
  const handleSubmit = async () => {
    if (!formData.nombre) return;
    setFormLoading(true);
    const data = new FormData();
    Object.entries(formData).forEach(([k, v]) => {
      if (k === 'cvFile' && v) data.append('cv', v);
      else if (k !== 'cvFile') data.append(k, v);
    });
    try {
      await axiosInstance.post(`/api/vacantes/${vacante.id}/candidatos`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      fetchCandidatos();
      resetAddForm();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al guardar candidato');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDescartar = async () => {
    if (!descartandoId) return;
    await updateEstado(descartandoId, 'Descartado', { motivo_descarte: motivoDescarte });
    setDescartandoId(null);
    setMotivoDescarte('');
  };

  const handleViewCV = async (candidato) => {
    try {
      const res = await axiosInstance.get(`/api/candidatos/${candidato.id}/cv`, { responseType: 'blob' });
      const contentType = res.headers['content-type'] || 'application/pdf';
      const blob = new Blob([res.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const isImage = contentType.startsWith('image/');
      setViewingCV({ url, type: isImage ? 'image' : 'pdf', filename: candidato.cv_filename || 'cv.pdf' });
    } catch {
      alert('Error al visualizar CV');
    }
  };

  const handleDownloadFromViewer = () => {
    if (!viewingCV) return;
    const a = document.createElement('a');
    a.href = viewingCV.url;
    a.download = viewingCV.filename;
    a.click();
  };

  // El backend marca al candidato como Contratado al registrar el ingreso
  const handleEmployeeCreated = () => {
    setShowCreateEmployee(false);
    onClose();
    onVacanteUpdated?.();
  };

  const candidatosActivos = candidatos.filter(c => c.estado !== 'Descartado' && c.estado !== 'Contratado');
  const candidatosDescartados = candidatos.filter(c => c.estado === 'Descartado');
  const prioStyle = getPrioridadStyle(vacante?.prioridad);
  const dias = vacante ? getDiasAbierta(vacante.fecha_creacion) : 0;

  return (
    <div className="cdrawer-root">
      {/* Drawer — fullscreen */}
      <motion.div
        className="cdrawer"
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 280 }}
      >
        {/* Header — single compact bar */}
        <div className="cdrawer-header">
          <button className="cdrawer-back-btn" onClick={onClose}>
            <FaArrowLeft /> Volver
          </button>
          <div className="cdrawer-header-main">
            <h2 className="cdrawer-title">{vacante?.titulo}</h2>
            <div className="cdrawer-meta">
              {vacante?.gerencia && <span>{vacante.gerencia}</span>}
              {vacante?.cargo && <><span className="cdrawer-meta-sep">·</span><span>{vacante.cargo}</span></>}
              {vacante?.prioridad && (
                <span className="cdrawer-prioridad" style={{ background: prioStyle.bg, color: prioStyle.color }}>
                  {vacante.prioridad}
                </span>
              )}
              <span className="cdrawer-dias">
                <FaClock style={{ fontSize: 10 }} /> {dias}d
              </span>
            </div>
          </div>
          <div className="cdrawer-header-actions">
            {process.env.NODE_ENV === 'development' && candidatosActivos.length >= 2 && (
              <button className="cdrawer-compare-btn" onClick={handleComparative} disabled={comparativeLoading}>
                {comparativeLoading ? <div className="cdrawer-spinner-sm" /> : <FaMagic />} Comparar
              </button>
            )}
            <button className="cdrawer-add-btn" onClick={() => { setShowAddForm(true); setAddStep('upload'); setFormData(EMPTY_FORM); }}>
              <FaPlus /> Candidato
            </button>
          </div>
        </div>

        {/* Add Candidate — inline push down */}
        <AnimatePresence>
          {showAddForm && (
              <motion.div
                className="cdrawer-add-form"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                style={{ overflow: 'hidden' }}
              >
                {addStep === 'upload' ? (
                  <div className="cdrawer-upload-step">
                    <label className="cdrawer-cv-dropzone">
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        onChange={e => { const file = e.target.files[0]; if (file) handleExtractCV(file); }}
                        style={{ display: 'none' }}
                        disabled={extracting}
                      />
                      {extracting ? (
                        <div className="cdrawer-extracting">
                          <div className="cdrawer-spinner" />
                          <span>Extrayendo datos del CV...</span>
                        </div>
                      ) : (
                        <>
                          <FaMagic style={{ fontSize: 22, color: '#7c3aed' }} />
                          <span className="cdrawer-dropzone-title">Subir CV y extraer con IA</span>
                          <span className="cdrawer-dropzone-hint">PDF, DOCX, JPG, PNG</span>
                        </>
                      )}
                    </label>
                    <div className="cdrawer-form-actions">
                      <button type="button" className="btn-secondary" onClick={resetAddForm}>Cancelar</button>
                      <button type="button" className="cdrawer-manual-link" onClick={() => setAddStep('form')}>
                        <FaEdit /> Llenar manualmente
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="cdrawer-form-grid">
                      <input className="input" placeholder="Nombre completo *" value={formData.nombre}
                        onChange={e => setFormData(p => ({ ...p, nombre: e.target.value }))} />
                      <input className="input" placeholder="Cédula" value={formData.cedula}
                        onChange={e => setFormData(p => ({ ...p, cedula: e.target.value.replace(/\D/g, '') }))} />
                      <input className="input" placeholder="Teléfono" value={formData.telefono_personal}
                        onChange={e => setFormData(p => ({ ...p, telefono_personal: e.target.value.replace(/\D/g, '') }))} />
                      <input className="input" type="email" placeholder="Email" value={formData.email_personal}
                        onChange={e => setFormData(p => ({ ...p, email_personal: e.target.value }))} />
                      <div className="cdrawer-form-full">
                        <textarea className="input" placeholder="Notas" value={formData.notas}
                          onChange={e => setFormData(p => ({ ...p, notas: e.target.value }))} rows={2} style={{ resize: 'none' }} />
                      </div>
                      {!formData.cvFile && (
                        <div className="cdrawer-form-full">
                          <label className="cdrawer-file-upload">
                            <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                              onChange={e => setFormData(p => ({ ...p, cvFile: e.target.files[0] }))} style={{ display: 'none' }} />
                            <span className="cdrawer-file-btn"><FaFileUpload /> Seleccionar CV</span>
                            <span className="cdrawer-file-name">PDF, DOCX, JPG, PNG</span>
                          </label>
                        </div>
                      )}
                      {formData.cvFile && (
                        <div className="cdrawer-form-full">
                          <span className="cdrawer-file-attached"><FaFileUpload /> {formData.cvFile.name}</span>
                        </div>
                      )}
                    </div>
                    <div className="cdrawer-form-actions">
                      <button type="button" className="btn-secondary" onClick={resetAddForm}>Cancelar</button>
                      <button type="button" className="btn-primary" disabled={formLoading || !formData.nombre} onClick={handleSubmit}>
                        <FaPlus /> {formLoading ? 'Guardando...' : 'Agregar'}
                      </button>
                    </div>
                  </div>
                )}
              </motion.div>
          )}
        </AnimatePresence>

        {/* Resultado comparativo */}
        <AnimatePresence>
          {comparativeResult && (
            <motion.div
              className="cdrawer-comparative"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <div className="cdrawer-comparative-header">
                <FaMagic style={{ color: '#7c3aed' }} /> Ranking comparativo
                <button className="cdrawer-comparative-close" onClick={() => setComparativeResult(null)}>&times;</button>
              </div>
              {comparativeResult.ranking?.map((r, i) => (
                <div key={r.candidato_id} className="cdrawer-comparative-item">
                  <span className="cdrawer-comparative-pos">#{r.posicion}</span>
                  <span className="cdrawer-comparative-name">{r.nombre}</span>
                  <span style={{ color: getCompatColor(r.compatibilidad), fontWeight: 600, fontSize: 12 }}>{r.compatibilidad}/10</span>
                  <span className="cdrawer-comparative-reason">{r.razon}</span>
                </div>
              ))}
              {comparativeResult.resumen_general && (
                <p className="cdrawer-comparative-summary">{comparativeResult.resumen_general}</p>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Kanban body */}
        <div className="cdrawer-body">
          {loading ? (
            <div className="cdrawer-loading">Cargando candidatos...</div>
          ) : candidatosActivos.length === 0 && candidatosDescartados.length === 0 ? (
            <div className="cdrawer-empty-state">
              <p>Sin candidatos registrados.</p>
              <button className="btn-primary" onClick={() => setShowAddForm(true)}>
                <FaPlus /> Agregar primer candidato
              </button>
            </div>
          ) : (
            <>
              <div className="cdrawer-kanban">
                {CANDIDATO_COLUMNS.map(col => {
                  const cards = candidatosActivos.filter(c => c.estado === col.key);
                  const isOver = dragOverCol === col.key;

                  return (
                    <div
                      key={col.key}
                      className={`cdrawer-col${isOver ? ' cdrawer-col--over' : ''}`}
                      style={{ '--col-color': col.color, '--col-light': col.light }}
                      onDragOver={e => handleDragOver(e, col.key)}
                      onDrop={e => handleDrop(e, col.key)}
                      onDragLeave={e => {
                        if (!e.currentTarget.contains(e.relatedTarget)) setDragOverCol(null);
                      }}
                    >
                      <div className="cdrawer-col-header" style={{ borderTopColor: col.color }}>
                        <div className="cdrawer-col-title">
                          <span className="cdrawer-col-dot" style={{ background: col.color }} />
                          <span style={{ color: col.dark }}>{col.label}</span>
                        </div>
                        <span className="cdrawer-col-badge" style={{ background: col.light, color: col.dark }}>
                          {cards.length}
                        </span>
                      </div>

                      <div className="cdrawer-cards">
                        {cards.length === 0 && (
                          <div className="cdrawer-empty">Arrastra aquí</div>
                        )}
                        <AnimatePresence>
                          {cards.map(candidato => (
                            <motion.div
                              key={candidato.id}
                              layout
                              initial={{ opacity: 0, y: 6 }}
                              animate={{ opacity: draggingId === candidato.id ? 0.3 : 1, y: 0 }}
                              exit={{ opacity: 0, scale: 0.95 }}
                              transition={{ duration: 0.15 }}
                              className="cdrawer-card"
                              draggable
                              onDragStart={e => handleDragStart(e, candidato.id)}
                              onDragEnd={() => { setDraggingId(null); setDragOverCol(null); }}
                            >
                              <div className="cdrawer-card-name">{candidato.nombre}</div>

                              <div className="cdrawer-card-details">
                                {candidato.cedula && (
                                  <div className="cdrawer-card-meta">
                                    <FaIdCard style={{ fontSize: 9 }} /> {candidato.cedula}
                                  </div>
                                )}
                                {candidato.telefono_personal && (
                                  <div className="cdrawer-card-meta">
                                    <FaPhone style={{ fontSize: 9 }} /> {candidato.telefono_personal}
                                  </div>
                                )}
                                {candidato.email_personal && (
                                  <div className="cdrawer-card-meta">
                                    <FaEnvelope style={{ fontSize: 9 }} /> {candidato.email_personal}
                                  </div>
                                )}
                              </div>
                              {candidato.notas && (
                                <div
                                  className={`cdrawer-card-notes ${expandedNotes[candidato.id] ? '' : 'cdrawer-card-notes--truncated'}`}
                                  onClick={() => setExpandedNotes(p => ({ ...p, [candidato.id]: !p[candidato.id] }))}
                                >
                                  {candidato.notas}
                                  {!expandedNotes[candidato.id] && candidato.notas.length > 60 && (
                                    <span className="cdrawer-card-notes-more"><FaChevronDown style={{ fontSize: 8 }} /></span>
                                  )}
                                </div>
                              )}

                              {/* Análisis IA */}
                              {candidato.analisis_ia && (
                                <button
                                  className="cdrawer-analysis-toggle"
                                  onClick={() => setExpandedAnalysis(expandedAnalysis === candidato.id ? null : candidato.id)}
                                >
                                  <FaMagic style={{ fontSize: 10 }} />
                                  <span style={{ color: getCompatColor(candidato.analisis_ia.compatibilidad) }}>
                                    {candidato.analisis_ia.compatibilidad}/10
                                  </span>
                                  <span>{candidato.analisis_ia.recomendacion}</span>
                                  {expandedAnalysis === candidato.id ? <FaChevronUp style={{ fontSize: 8, marginLeft: 'auto' }} /> : <FaChevronDown style={{ fontSize: 8, marginLeft: 'auto' }} />}
                                </button>
                              )}
                              <AnimatePresence>
                                {expandedAnalysis === candidato.id && candidato.analisis_ia && (
                                  <motion.div
                                    className="cdrawer-analysis-body"
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.15 }}
                                    style={{ overflow: 'hidden' }}
                                  >
                                    <p className="cdrawer-analysis-summary">{candidato.analisis_ia.resumen}</p>
                                    {candidato.analisis_ia.fortalezas?.length > 0 && (
                                      <div className="cdrawer-analysis-list cdrawer-analysis-list--green">
                                        {candidato.analisis_ia.fortalezas.map((f, i) => <span key={i}>+ {f}</span>)}
                                      </div>
                                    )}
                                    {candidato.analisis_ia.debilidades?.length > 0 && (
                                      <div className="cdrawer-analysis-list cdrawer-analysis-list--red">
                                        {candidato.analisis_ia.debilidades.map((d, i) => <span key={i}>- {d}</span>)}
                                      </div>
                                    )}
                                  </motion.div>
                                )}
                              </AnimatePresence>

                              <div className="cdrawer-card-actions">
                                <button
                                  className="cdrawer-card-btn cdrawer-card-btn--purple"
                                  title={candidato.analisis_ia ? 'Re-analizar con IA' : 'Analizar con IA'}
                                  onClick={() => handleAnalyze(candidato.id)}
                                  disabled={analyzingId === candidato.id}
                                >
                                  {analyzingId === candidato.id ? <div className="cdrawer-spinner-sm" /> : <FaMagic />}
                                </button>
                                {candidato.cv_path && (
                                  <button
                                    className="cdrawer-card-btn cdrawer-card-btn--blue"
                                    title="Ver CV"
                                    onClick={() => handleViewCV(candidato)}
                                  >
                                    <FaEye />
                                  </button>
                                )}
                                {col.key === 'Seleccionado' && (
                                  <button
                                    className="cdrawer-card-btn cdrawer-card-btn--green"
                                    title="Crear ficha de empleado"
                                    onClick={() => {
                                      setCandidatoParaEmpleado(candidato);
                                      setShowCreateEmployee(true);
                                    }}
                                  >
                                    <FaUserPlus />
                                  </button>
                                )}
                                <button
                                  className="cdrawer-card-btn cdrawer-card-btn--red"
                                  title="Descartar"
                                  onClick={() => setDescartandoId(candidato.id)}
                                >
                                  <FaTimesCircle />
                                </button>
                              </div>
                            </motion.div>
                          ))}
                        </AnimatePresence>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Descartados */}
              {candidatosDescartados.length > 0 && (
                <div className="cdrawer-descartados">
                  <button
                    className="cdrawer-descartados-toggle"
                    onClick={() => setShowDescartados(v => !v)}
                  >
                    <FaTimesCircle style={{ color: '#dc2626', fontSize: 11 }} />
                    Descartados ({candidatosDescartados.length})
                    <span className="cdrawer-descartados-chevron">{showDescartados ? '▲' : '▼'}</span>
                  </button>
                  <AnimatePresence>
                    {showDescartados && (
                      <motion.div
                        className="cdrawer-descartados-list"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.18 }}
                        style={{ overflow: 'hidden' }}
                      >
                        {candidatosDescartados.map(c => (
                          <div key={c.id} className="cdrawer-descartado-item">
                            <span className="cdrawer-descartado-name">{c.nombre}</span>
                            {c.motivo_descarte && (
                              <span className="cdrawer-descartado-motivo">— {c.motivo_descarte}</span>
                            )}
                          </div>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </>
          )}
        </div>
      </motion.div>

      {/* Visor de CV */}
      <AnimatePresence>
        {viewingCV && (
          <motion.div
            className="cdrawer-viewer-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => { window.URL.revokeObjectURL(viewingCV.url); setViewingCV(null); }}
          >
            <motion.div
              className="cdrawer-viewer"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="cdrawer-viewer-header">
                <span className="cdrawer-viewer-filename">{viewingCV.filename}</span>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="cdrawer-viewer-btn" onClick={handleDownloadFromViewer} title="Descargar">
                    <FaDownload />
                  </button>
                  <button className="cdrawer-viewer-btn" onClick={() => { window.URL.revokeObjectURL(viewingCV.url); setViewingCV(null); }} title="Cerrar">
                    &times;
                  </button>
                </div>
              </div>
              <div className="cdrawer-viewer-body">
                {viewingCV.type === 'image' ? (
                  <img src={viewingCV.url} alt="CV" className="cdrawer-viewer-img" />
                ) : (
                  <iframe src={viewingCV.url} className="cdrawer-viewer-iframe" title="CV" />
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal descartar (dentro del drawer) */}
      <AnimatePresence>
        {descartandoId && (
          <>
            <motion.div
              className="cdrawer-modal-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />
            <motion.div
              className="cdrawer-modal"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.15 }}
            >
              <h3 className="cdrawer-modal-title">Descartar candidato</h3>
              <p style={{ fontSize: 14, color: '#64748b', marginBottom: 12 }}>
                ¿Descartar a <strong style={{ color: '#1e293b' }}>{candidatos.find(c => c.id === descartandoId)?.nombre}</strong>?
              </p>
              <textarea
                className="input"
                placeholder="Motivo del descarte (opcional)"
                value={motivoDescarte}
                onChange={e => setMotivoDescarte(e.target.value)}
                rows={3}
                style={{ resize: 'none' }}
              />
              <div className="cdrawer-modal-actions">
                <button
                  className="btn-secondary"
                  onClick={() => { setDescartandoId(null); setMotivoDescarte(''); }}
                >
                  Cancelar
                </button>
                <button className="btn-danger" onClick={handleDescartar}>
                  <FaTimesCircle /> Descartar
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Modal crear empleado */}
      {showCreateEmployee && (
        <NewEmployeeModal
          onClose={() => setShowCreateEmployee(false)}
          onEmployeeAdded={handleEmployeeCreated}
          initialVacanteId={vacante?.id}
          initialCandidateData={candidatoParaEmpleado}
        />
      )}
    </div>
  );
};

export default CandidatosKanbanPanel;
